import React, { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatDate, formatRelativeDate } from '@/lib/utils'
import { useIsMobile } from '@/lib/useIsMobile'
import {
  Star, Send, X, Plus, TrendingUp, MessageSquare,
  ThumbsUp, Check, ArrowUpRight, ChevronDown, Filter,
  Calendar, Briefcase, Wrench, MapPin, Building2, User,
  AlertCircle, Edit2, Trash2, FileText,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

const PLATFORMS = [
  { id: 'google',          label: 'Google',          color: '#4285f4', icon: '🔵' },
  { id: 'facebook',        label: 'Facebook',        color: '#1877f2', icon: '🔷' },
  { id: 'yelp',            label: 'Yelp',            color: '#ff1a1a', icon: '🔴' },
  { id: 'trustpilot',      label: 'Trustpilot',      color: '#00b67a', icon: '🟢' },
  { id: 'bbb',             label: 'BBB',             color: '#003f8a', icon: '🔹' },
  { id: 'angi',            label: 'Angi',            color: '#ff6b00', icon: '🟠' },
  { id: 'homeadvisor',     label: 'HomeAdvisor',     color: '#f37021', icon: '🟧' },
  { id: 'internal_survey', label: 'Internal Survey', color: '#7c3aed', icon: '📋' },
  { id: 'manual_entry',    label: 'Manual Entry',    color: 'var(--ink-tertiary)', icon: '✏️' },
]

const STATUSES: Array<{ id: string; label: string; color: string; bg: string }> = [
  { id: 'new',            label: 'New',            color: 'var(--accent-text)', bg: 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)' },
  { id: 'needs_response', label: 'Needs response', color: 'var(--warning)',     bg: 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)' },
  { id: 'draft_response', label: 'Draft',          color: 'var(--info)',        bg: 'color-mix(in srgb, var(--info) var(--status-tint-fill), transparent)' },
  { id: 'responded',      label: 'Responded',      color: 'var(--positive)',    bg: 'color-mix(in srgb, var(--positive) var(--status-tint-fill), transparent)' },
  { id: 'escalated',      label: 'Escalated',      color: 'var(--danger)',      bg: 'color-mix(in srgb, var(--danger) var(--status-tint-fill), transparent)' },
  { id: 'resolved',       label: 'Resolved',       color: 'var(--ink-secondary)', bg: 'var(--border-subtle)' },
  { id: 'archived',       label: 'Archived',       color: 'var(--ink-tertiary)', bg: 'var(--hover-bg)' },
]

const SENTIMENTS = [
  { id: 'positive', label: 'Positive', color: 'var(--positive)' },
  { id: 'neutral',  label: 'Neutral',  color: 'var(--ink-tertiary)' },
  { id: 'negative', label: 'Negative', color: 'var(--danger)' },
]

const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'warm',         label: 'Warm' },
  { id: 'short',        label: 'Short' },
  { id: 'apology',      label: 'Apology' },
]

const DATE_RANGES = [
  { id: 'all', label: 'All time', days: null as number | null },
  { id: '7d',  label: '7 days',   days: 7 },
  { id: '30d', label: '30 days',  days: 30 },
  { id: '90d', label: '90 days',  days: 90 },
]

const NPS_CATEGORIES = [
  { label: 'Promoter', range: [9, 10], color: 'var(--positive)' },
  { label: 'Passive', range: [7, 8], color: 'var(--warning)' },
  { label: 'Detractor', range: [0, 6], color: 'var(--danger)' },
]

function getNPSCategory(score: number) {
  return NPS_CATEGORIES.find(c => score >= c.range[0] && score <= c.range[1]) || NPS_CATEGORIES[2]
}

function StarRating({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} style={{ width: size, height: size, color: i <= Math.round(rating) ? 'var(--warning)' : 'var(--border-default)', fill: i <= Math.round(rating) ? 'var(--warning)' : 'none' }} />
      ))}
    </div>
  )
}

function RequestReviewModal({ onClose, onCreated, contacts }: any) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({ contact_id: '', contact_name: '', contact_email: '', channel: 'email', platforms: ['google'] })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleContactSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const c = contacts.find((c: any) => c.id === e.target.value)
    if (c) setForm(p => ({ ...p, contact_id: c.id, contact_name: `${c.first_name} ${c.last_name || ''}`.trim(), contact_email: c.email || '' }))
  }

  const togglePlatform = (id: string) => {
    setForm(p => ({ ...p, platforms: p.platforms.includes(id) ? p.platforms.filter(x => x !== id) : [...p.platforms, id] }))
  }

  const handleSave = async () => {
    if (!form.contact_name.trim()) { toast.error('Please select a contact'); return }
    if (form.platforms.length === 0) { toast.error('Select at least one platform'); return }
    setSaving(true)
    const { error } = await supabase.from('review_requests').insert({
      contact_id: form.contact_id || '00000000-0000-0000-0000-000000000000',
      contact_name: form.contact_name, contact_email: form.contact_email,
      channel: form.channel, platforms: form.platforms,
      status: 'sent', sent_at: new Date().toISOString(), workspace_id: workspace!.id,
    })
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success(`Review request logged for ${form.contact_name}`)
    onCreated(); onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Request a review</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Customer *</label>
            <select onChange={handleContactSelect} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select a contact...</option>
              {contacts.map((c: any) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.email ? `— ${c.email}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Send via</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['email', 'sms'].map(ch => (
                <button key={ch} onClick={() => set('channel', ch)}
                  style={{ flex: 1, padding: '9px', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: form.channel === ch ? 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)' : 'var(--hover-bg)', color: form.channel === ch ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: form.channel === ch ? '1px solid color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : '1px solid var(--border-subtle)' }}>
                  {ch === 'email' ? '✉️ Email' : '📱 SMS'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Review platforms</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {PLATFORMS.map(p => (
                <button key={p.id} onClick={() => togglePlatform(p.id)}
                  style={{ padding: '7px 13px', borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: form.platforms.includes(p.id) ? `color-mix(in srgb, ${p.color} var(--status-tint-fill), transparent)` : 'var(--hover-bg)', color: form.platforms.includes(p.id) ? p.color : 'var(--ink-tertiary)', border: form.platforms.includes(p.id) ? `1px solid color-mix(in srgb, ${p.color} var(--status-tint-edge), transparent)` : '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 5 }}>
                  {p.icon} {p.label}
                  {form.platforms.includes(p.id) && <Check style={{ width: 11, height: 11 }} />}
                </button>
              ))}
            </div>
          </div>
          <div style={{ background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)', borderRadius: 10, padding: '12px 14px' }}>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.6 }}>
              Records that you've asked <strong style={{ color: 'var(--ink-primary)' }}>{form.contact_name || 'the customer'}</strong> for a review on {form.platforms.length > 0 ? form.platforms.join(', ') : 'the selected platforms'} (preferred channel: {form.channel}). Automated email and SMS delivery aren't wired yet — for now, send the ask yourself and use this to track that you did.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Logging...' : <><Send style={{ width: 14, height: 14 }} /> Log request</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function SectionHeader({ label }: { label: string }) {
  return (
    <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '14px 0 8px', paddingBottom: 6, borderBottom: '1px solid var(--border-subtle)' }}>{label}</p>
  )
}

function AddReviewModal({ onClose, onCreated, vendors, serviceLines, locations, profiles, fieldJobs }: any) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({
    platform: 'google',
    rating: 5,
    reviewer_name: '',
    review_text: '',
    review_url: '',
    sentiment: '',
    published_at: new Date().toISOString().split('T')[0],
    field_job_id: '',
    vendor_id: '',
    location_id: '',
    service_line_id: '',
    assigned_owner_id: '',
    internal_notes: '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleSave = async () => {
    setSaving(true)
    const { error } = await supabase.from('review_records').insert({
      platform: form.platform,
      rating: form.rating,
      reviewer_name: form.reviewer_name || null,
      review_text: form.review_text || null,
      review_url: form.review_url || null,
      sentiment: form.sentiment || null,
      published_at: form.published_at || null,
      field_job_id: form.field_job_id || null,
      vendor_id: form.vendor_id || null,
      location_id: form.location_id || null,
      service_line_id: form.service_line_id || null,
      assigned_owner_id: form.assigned_owner_id || null,
      internal_notes: form.internal_notes || null,
      status: 'new',
      workspace_id: workspace!.id,
    })
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Review added')
    onCreated(); onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Add review</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        <SectionHeader label="Review details" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Platform</label>
              <select value={form.platform} onChange={e => set('platform', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date</label>
              <input type="date" value={form.published_at} onChange={e => set('published_at', e.target.value)} style={{ ...inp, colorScheme: 'dark' }} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Rating</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => set('rating', n)}
                  style={{ width: 36, height: 36, borderRadius: 9, background: form.rating >= n ? 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)' : 'var(--hover-bg)', border: form.rating >= n ? '1px solid color-mix(in srgb, var(--warning) var(--status-tint-edge), transparent)' : '1px solid var(--border-subtle)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Star style={{ width: 16, height: 16, color: form.rating >= n ? 'var(--warning)' : 'var(--border-default)', fill: form.rating >= n ? 'var(--warning)' : 'none' }} />
                </button>
              ))}
              <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--warning)', marginLeft: 4, alignSelf: 'center' }}>{form.rating}.0</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reviewer name</label>
              <input value={form.reviewer_name} onChange={e => set('reviewer_name', e.target.value)} placeholder="Jane D." style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sentiment</label>
              <select value={form.sentiment} onChange={e => set('sentiment', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Auto / unset</option>
                {SENTIMENTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Review text</label>
            <textarea value={form.review_text} onChange={e => set('review_text', e.target.value)} placeholder="What did they say?" rows={3} style={{ ...inp, resize: 'none', lineHeight: 1.5 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Review URL</label>
            <input value={form.review_url} onChange={e => set('review_url', e.target.value)} placeholder="https://google.com/maps/..." style={inp} />
          </div>
        </div>

        <SectionHeader label="Links" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Field job</label>
            <select value={form.field_job_id} onChange={e => set('field_job_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">None</option>
              {(fieldJobs || []).map((j: any) => <option key={j.id} value={j.id}>{j.job_number} — {j.title || 'Untitled'}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Service line</label>
            <select value={form.service_line_id} onChange={e => set('service_line_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">None</option>
              {(serviceLines || []).map((sl: any) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Vendor</label>
            <select value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">None — internal</option>
              {(vendors || []).map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Location</label>
            <select value={form.location_id} onChange={e => set('location_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">None</option>
              {(locations || []).map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        </div>

        <SectionHeader label="Internal" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Assigned owner</label>
            <select value={form.assigned_owner_id} onChange={e => set('assigned_owner_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Unassigned</option>
              {(profiles || []).map((p: any) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Internal notes</label>
            <textarea value={form.internal_notes} onChange={e => set('internal_notes', e.target.value)} placeholder="Context for the team — not shown to the reviewer" rows={2} style={{ ...inp, resize: 'none', lineHeight: 1.5 }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Saving...' : 'Add review'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function TemplateModal({ onClose, onSaved, existing, serviceLines }: any) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({
    name: existing?.name || '',
    rating_type: existing?.rating_type || 5,
    service_line_id: existing?.service_line_id || '',
    platform: existing?.platform || '',
    tone: existing?.tone || '',
    template_body: existing?.template_body || '',
    is_active: existing?.is_active ?? true,
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: any) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return }
    if (!form.template_body.trim()) { toast.error('Template body is required'); return }
    setSaving(true)
    const payload = {
      name: form.name,
      rating_type: form.rating_type,
      service_line_id: form.service_line_id || null,
      platform: form.platform || null,
      tone: form.tone || null,
      template_body: form.template_body,
      is_active: form.is_active,
    }
    const { error } = existing
      ? await supabase.from('review_response_templates').update(payload).eq('id', existing.id)
      : await supabase.from('review_response_templates').insert({ ...payload, workspace_id: workspace!.id })
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success(existing ? 'Template updated' : 'Template created')
    onSaved(); onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>{existing ? 'Edit template' : 'New template'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="5-star thanks (HVAC)" style={inp} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Rating bucket *</label>
              <select value={form.rating_type} onChange={e => set('rating_type', parseInt(e.target.value))} style={{ ...inp, cursor: 'pointer' }}>
                {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} ★</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tone</label>
              <select value={form.tone} onChange={e => set('tone', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Any</option>
                {TONES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Service line</label>
              <select value={form.service_line_id} onChange={e => set('service_line_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Any service line</option>
                {(serviceLines || []).map((sl: any) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Platform</label>
              <select value={form.platform} onChange={e => set('platform', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Any platform</option>
                {PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Template body *</label>
            <textarea value={form.template_body} onChange={e => set('template_body', e.target.value)}
              placeholder="Thanks {{reviewer_name}} for your {{rating}}-star review of our {{service_line}} work..."
              rows={6}
              style={{ ...inp, resize: 'vertical', lineHeight: 1.6, fontFamily: 'inherit' }} />
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '6px 0 0' }}>
              Placeholders like <code>{'{{reviewer_name}}'}</code> are stored verbatim today; expansion is a Phase 4 enhancement.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--surface)', borderRadius: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-secondary)' }}>Active</span>
            <button onClick={() => set('is_active', !form.is_active)} aria-label="Toggle active"
              style={{ width: 32, height: 18, borderRadius: 100, background: form.is_active ? 'var(--positive)' : 'var(--border-default)', border: 'none', cursor: 'pointer', position: 'relative' }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', position: 'absolute', top: 2, left: form.is_active ? 16 : 2, transition: 'left 150ms' }} />
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Saving...' : (existing ? 'Save changes' : 'Create template')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function ReviewDrawer({ review, onClose, onUpdate, onDelete, vendors, serviceLines, locations, profiles, fieldJobs, templates }: any) {
  const navigate = useNavigate()
  const [response, setResponse] = useState(review.our_response || '')
  const [internalNotes, setInternalNotes] = useState(review.internal_notes || '')
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)

  useEffect(() => {
    setResponse(review.our_response || '')
    setInternalNotes(review.internal_notes || '')
  }, [review.id])

  const platform = PLATFORMS.find(p => p.id === review.platform)
  const statusCfg = STATUSES.find(s => s.id === review.status) || STATUSES[0]
  const sentimentCfg = SENTIMENTS.find(s => s.id === review.sentiment)

  const applicableTemplates = (templates || []).filter((t: any) =>
    t.is_active &&
    t.rating_type === Math.round(review.rating) &&
    (!t.platform || t.platform === review.platform) &&
    (!t.service_line_id || t.service_line_id === review.service_line_id)
  )

  const saveResponse = (status?: string) => {
    onUpdate(review.id, {
      our_response: response,
      status: status || (response ? 'responded' : review.status),
      responded_at: response ? new Date().toISOString() : null,
    })
    if (status) toast.success(`Marked ${STATUSES.find(s => s.id === status)?.label || status}`)
    else toast.success('Response saved')
  }

  return (
    <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(480px, 100vw)', background: 'var(--surface)', borderLeft: '1px solid var(--border-subtle)', zIndex: 150, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `color-mix(in srgb, ${platform?.color || 'var(--accent)'} var(--status-tint-fill), transparent)`, border: `1px solid color-mix(in srgb, ${platform?.color || 'var(--accent)'} var(--status-tint-strong), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
              {platform?.icon || '⭐'}
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{review.reviewer_name || 'Anonymous'}</p>
              <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{platform?.label} · {formatDate(review.published_at || review.created_at, 'MMM d, yyyy')}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => { if (confirm('Delete this review?')) onDelete(review.id) }} aria-label="Delete review"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', borderRadius: 6 }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
              <Trash2 style={{ width: 14, height: 14 }} />
            </button>
            <button onClick={onClose} aria-label="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex' }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <StarRating rating={review.rating} size={14} />
          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--warning)' }}>{review.rating}</span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: statusCfg.bg, color: statusCfg.color }}>{statusCfg.label}</span>
          {sentimentCfg && (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: `color-mix(in srgb, ${sentimentCfg.color} var(--status-tint-dim), transparent)`, color: sentimentCfg.color, border: `1px solid color-mix(in srgb, ${sentimentCfg.color} var(--status-tint-strong), transparent)` }}>{sentimentCfg.label}</span>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {review.review_text && (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Review</p>
            <p style={{ fontSize: 13, color: 'var(--ink-primary)', margin: 0, lineHeight: 1.6 }}>{review.review_text}</p>
            {review.review_url && (
              <a href={review.review_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--accent)', textDecoration: 'none', fontWeight: 600, marginTop: 8 }}>
                Open original <ArrowUpRight style={{ width: 11, height: 11 }} />
              </a>
            )}
          </div>
        )}

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Your response</p>
            {applicableTemplates.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowTemplatePicker(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--accent-text)', background: 'color-mix(in srgb, var(--accent) var(--status-tint-dim), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontWeight: 600 }}>
                  <FileText style={{ width: 11, height: 11 }} /> Use template <ChevronDown style={{ width: 10, height: 10 }} />
                </button>
                {showTemplatePicker && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 9, padding: 4, zIndex: 60, minWidth: 220, maxHeight: 220, overflowY: 'auto' }}>
                    {applicableTemplates.map((t: any) => (
                      <button key={t.id} onClick={() => { setResponse(t.template_body); setShowTemplatePicker(false) }}
                        style={{ width: '100%', textAlign: 'left', padding: '7px 10px', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 6, fontSize: 12, color: 'var(--ink-primary)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <p style={{ margin: 0, fontWeight: 600 }}>{t.name}</p>
                        {t.tone && <p style={{ margin: 0, fontSize: 10, color: 'var(--ink-tertiary)' }}>{t.tone}</p>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <textarea value={response} onChange={e => setResponse(e.target.value)} placeholder="Compose your response..."
            rows={5}
            style={{ width: '100%', padding: '10px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box', fontFamily: 'inherit' }}
            onFocus={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)'}
            onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={() => saveResponse()} disabled={!response.trim()} className="btn-primary" style={{ fontSize: 12, padding: '7px 14px' }}>
              Save response
            </button>
            <button onClick={() => onUpdate(review.id, { status: 'draft_response' })} className="btn-ghost" style={{ fontSize: 12, padding: '7px 14px' }}>
              Save as draft
            </button>
            <button onClick={() => onUpdate(review.id, { status: 'escalated' })} className="btn-ghost" style={{ fontSize: 12, padding: '7px 14px', color: 'var(--danger)' }}>
              Escalate
            </button>
          </div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Links</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Briefcase style={{ width: 12, height: 12, color: 'var(--ink-tertiary)' }} />
              <select value={review.field_job_id || ''} onChange={e => onUpdate(review.id, { field_job_id: e.target.value || null })}
                style={{ flex: 1, padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                <option value="">No field job</option>
                {(fieldJobs || []).map((j: any) => <option key={j.id} value={j.id}>{j.job_number} — {j.title || 'Untitled'}</option>)}
              </select>
              {review.field_job_id && (
                <button onClick={() => navigate(`/app/field?job=${review.field_job_id}`)} aria-label="Open job"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 4, display: 'flex' }}>
                  <ArrowUpRight style={{ width: 13, height: 13 }} />
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Wrench style={{ width: 12, height: 12, color: 'var(--ink-tertiary)' }} />
              <select value={review.vendor_id || ''} onChange={e => onUpdate(review.id, { vendor_id: e.target.value || null })}
                style={{ flex: 1, padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                <option value="">No vendor (internal)</option>
                {(vendors || []).map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
              {review.vendor_id && (
                <button onClick={() => navigate(`/app/vendors/${review.vendor_id}`)} aria-label="Open vendor"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--warning)', padding: 4, display: 'flex' }}>
                  <ArrowUpRight style={{ width: 13, height: 13 }} />
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <MapPin style={{ width: 12, height: 12, color: 'var(--ink-tertiary)' }} />
              <select value={review.location_id || ''} onChange={e => onUpdate(review.id, { location_id: e.target.value || null })}
                style={{ flex: 1, padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                <option value="">No location</option>
                {(locations || []).map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Building2 style={{ width: 12, height: 12, color: 'var(--ink-tertiary)' }} />
              <select value={review.service_line_id || ''} onChange={e => onUpdate(review.id, { service_line_id: e.target.value || null })}
                style={{ flex: 1, padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                <option value="">No service line</option>
                {(serviceLines || []).map((sl: any) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <User style={{ width: 12, height: 12, color: 'var(--ink-tertiary)' }} />
              <select value={review.assigned_owner_id || ''} onChange={e => onUpdate(review.id, { assigned_owner_id: e.target.value || null })}
                style={{ flex: 1, padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                <option value="">Unassigned</option>
                {(profiles || []).map((p: any) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Sentiment</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => onUpdate(review.id, { sentiment: null })}
              style={{ flex: 1, padding: '6px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: !review.sentiment ? 'var(--border-subtle)' : 'var(--surface)', color: !review.sentiment ? 'var(--ink-primary)' : 'var(--ink-tertiary)', border: !review.sentiment ? '1px solid var(--border-default)' : '1px solid var(--border-subtle)' }}>
              Unset
            </button>
            {SENTIMENTS.map(s => (
              <button key={s.id} onClick={() => onUpdate(review.id, { sentiment: s.id })}
                style={{ flex: 1, padding: '6px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: review.sentiment === s.id ? `color-mix(in srgb, ${s.color} var(--status-tint-fill), transparent)` : 'var(--surface)', color: review.sentiment === s.id ? s.color : 'var(--ink-tertiary)', border: review.sentiment === s.id ? `1px solid color-mix(in srgb, ${s.color} var(--status-tint-edge), transparent)` : '1px solid var(--border-subtle)' }}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Internal notes</p>
          <textarea value={internalNotes} onChange={e => setInternalNotes(e.target.value)}
            onBlur={() => { if (internalNotes !== (review.internal_notes || '')) onUpdate(review.id, { internal_notes: internalNotes || null }) }}
            placeholder="Context for the team — not shown to the reviewer"
            rows={3}
            style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: 'var(--ink-secondary)', resize: 'none', lineHeight: 1.6, fontFamily: 'inherit' }} />
        </div>
      </div>

      <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
        {['needs_response', 'resolved', 'archived'].map(s => {
          const cfg = STATUSES.find(x => x.id === s)!
          return (
            <button key={s} onClick={() => onUpdate(review.id, { status: s })}
              disabled={review.status === s}
              style={{ flex: 1, padding: '7px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: review.status === s ? 'default' : 'pointer', background: review.status === s ? cfg.bg : 'var(--hover-bg)', color: review.status === s ? cfg.color : 'var(--ink-tertiary)', border: review.status === s ? `1px solid color-mix(in srgb, ${cfg.color} var(--status-tint-edge), transparent)` : '1px solid var(--border-subtle)', opacity: review.status === s ? 1 : 0.85 }}>
              Mark {cfg.label.toLowerCase()}
            </button>
          )
        })}
      </div>
    </motion.div>
  )
}

function ReviewCard({ review, onClick }: { review: any; onClick: () => void }) {
  const platform = PLATFORMS.find(p => p.id === review.platform)
  const statusCfg = STATUSES.find(s => s.id === review.status) || STATUSES[0]
  const sentimentCfg = SENTIMENTS.find(s => s.id === review.sentiment)
  const owner = review.assigned_owner

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} onClick={onClick}
      style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: '16px 18px', cursor: 'pointer', transition: 'border-color 150ms ease' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10, gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `color-mix(in srgb, ${platform?.color || 'var(--accent)'} var(--status-tint-fill), transparent)`, border: `1px solid color-mix(in srgb, ${platform?.color || 'var(--accent)'} var(--status-tint-strong), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
            {platform?.icon || '⭐'}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{review.reviewer_name || 'Anonymous'}</p>
            <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{platform?.label} · {formatDate(review.published_at || review.created_at, 'MMM d, yyyy')}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <StarRating rating={review.rating} />
          <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--warning)' }}>{review.rating}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: statusCfg.bg, color: statusCfg.color }}>{statusCfg.label}</span>
          {sentimentCfg && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: `color-mix(in srgb, ${sentimentCfg.color} var(--status-tint-dim), transparent)`, color: sentimentCfg.color }}>{sentimentCfg.label}</span>
          )}
        </div>
      </div>
      {review.review_text && <p style={{ fontSize: 13, color: 'var(--ink-secondary)', lineHeight: 1.6, margin: '0 0 10px' }}>{review.review_text}</p>}
      {review.our_response && (
        <div style={{ background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)', borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
          <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent-text)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Your response</p>
          <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5 }}>{review.our_response}</p>
        </div>
      )}
      {(owner?.full_name || review.vendor?.name || review.field_job?.job_number || review.service_line?.name) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 11, color: 'var(--ink-tertiary)' }}>
          {owner?.full_name && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><User style={{ width: 10, height: 10 }} />{owner.full_name}</span>}
          {review.vendor?.name && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Wrench style={{ width: 10, height: 10 }} />{review.vendor.name}</span>}
          {review.field_job?.job_number && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'monospace' }}><Briefcase style={{ width: 10, height: 10 }} />{review.field_job.job_number}</span>}
          {review.service_line?.name && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{review.service_line.name}</span>}
        </div>
      )}
    </motion.div>
  )
}

export default function ReputationHub() {
  const { workspace } = useAuth()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [searchParams, setSearchParams] = useSearchParams()
const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview')

useEffect(() => {
  const tab = searchParams.get('tab')
  if (tab && tab !== activeTab) setActiveTab(tab)
  else if (!tab && activeTab !== 'overview') setActiveTab('overview')
}, [searchParams])
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [showAddReviewModal, setShowAddReviewModal] = useState(false)
  const [platformFilter, setPlatformFilter] = useState('all')
  const [ratingFilter, setRatingFilter] = useState('all')
  const [sentimentFilter, setSentimentFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateRangeFilter, setDateRangeFilter] = useState('all')
  const [vendorFilter, setVendorFilter] = useState('all')
  const [locationFilter, setLocationFilter] = useState('all')
  const [serviceLineFilter, setServiceLineFilter] = useState('all')
  const [ownerFilter, setOwnerFilter] = useState('all')
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [selectedReview, setSelectedReview] = useState<any>(null)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<any>(null)

  const switchTab = (id: string) => {
    setActiveTab(id)
    setSearchParams(id !== 'overview' ? { tab: id } : {})
  }

  const { data: reviews = [], isLoading: reviewsLoading } = useQuery({
    queryKey: ['reviews', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('review_records')
        .select('*, vendor:vendors(id, name), service_line:service_lines(id, name), location:locations(id, name), field_job:field_jobs(id, job_number, title), assigned_owner:profiles!review_records_assigned_owner_id_fkey(id, full_name)')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('vendors').select('id, name').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: serviceLines = [] } = useQuery({
    queryKey: ['service-lines-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('service_lines').select('id, name').eq('workspace_id', workspace.id).eq('is_active', true).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: locations = [] } = useQuery({
    queryKey: ['locations-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('locations').select('id, name').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name').eq('workspace_id', workspace.id).order('full_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: fieldJobs = [] } = useQuery({
    queryKey: ['field-jobs-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('field_jobs').select('id, job_number, title').eq('workspace_id', workspace.id).order('created_at', { ascending: false }).limit(200)
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: templates = [] } = useQuery({
    queryKey: ['review-templates', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('review_response_templates')
        .select('*, service_line:service_lines(id, name)')
        .eq('workspace_id', workspace.id)
        .order('rating_type')
        .order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: requests = [] } = useQuery({
    queryKey: ['review-requests', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('review_requests').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: nps = [] } = useQuery({
    queryKey: ['nps', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('nps_surveys').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-reputation', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, first_name, last_name, email, phone').eq('workspace_id', workspace.id).order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const updateReviewMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from('review_records').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reviews'] }),
    onError: (e: any) => toast.error(`Update failed: ${e.message}`),
  })

  const deleteReviewMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('review_records').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['reviews'] }); toast.success('Review deleted'); setSelectedReview(null) },
    onError: () => toast.error('Failed to delete'),
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('review_response_templates').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['review-templates'] }); toast.success('Template deleted') },
    onError: () => toast.error('Failed to delete'),
  })

  const handleReviewUpdate = (id: string, updates: Record<string, any>) => {
    updateReviewMutation.mutate({ id, updates })
    if (selectedReview?.id === id) setSelectedReview((prev: any) => ({ ...prev, ...updates }))
  }

  const metrics = useMemo(() => {
    if (reviews.length === 0) return { avg: 0, total: 0, newThisWeek: 0, newThisMonth: 0, pending: 0, negative: 0, positive: 0, fiveStar: 0, responded: 0, byPlatform: {} as Record<string, { count: number; avg: number; sum: number }> }
    const now = Date.now()
    const weekAgo = now - 7 * 86400000
    const monthAgo = now - 30 * 86400000
    const PENDING_STATUSES = ['new', 'needs_response', 'draft_response']
    const avg = reviews.reduce((s: number, r: any) => s + r.rating, 0) / reviews.length
    const fiveStar = reviews.filter((r: any) => r.rating === 5).length
    const responded = reviews.filter((r: any) => r.status === 'responded').length
    const newThisWeek = reviews.filter((r: any) => new Date(r.created_at).getTime() >= weekAgo).length
    const newThisMonth = reviews.filter((r: any) => new Date(r.created_at).getTime() >= monthAgo).length
    const pending = reviews.filter((r: any) => PENDING_STATUSES.includes(r.status)).length
    const negative = reviews.filter((r: any) => r.rating <= 2 || r.sentiment === 'negative').length
    const positive = reviews.filter((r: any) => r.rating >= 4 || r.sentiment === 'positive').length
    const byPlatform: Record<string, { count: number; avg: number; sum: number }> = {}
    reviews.forEach((r: any) => {
      if (!byPlatform[r.platform]) byPlatform[r.platform] = { count: 0, avg: 0, sum: 0 }
      byPlatform[r.platform].count++
      byPlatform[r.platform].sum += r.rating
      byPlatform[r.platform].avg = byPlatform[r.platform].sum / byPlatform[r.platform].count
    })
    return { avg: Math.round(avg * 10) / 10, total: reviews.length, newThisWeek, newThisMonth, pending, negative, positive, fiveStar, responded, byPlatform }
  }, [reviews])

  const npsScore = useMemo(() => {
    const responded = nps.filter((n: any) => n.score !== null && n.score !== undefined)
    if (responded.length === 0) return null
    const promoters = responded.filter((n: any) => n.score >= 9).length
    const detractors = responded.filter((n: any) => n.score <= 6).length
    return Math.round(((promoters - detractors) / responded.length) * 100)
  }, [nps])

  const filteredReviews = useMemo(() => {
    let list = [...reviews]
    if (platformFilter !== 'all') list = list.filter((r: any) => r.platform === platformFilter)
    if (ratingFilter !== 'all') list = list.filter((r: any) => r.rating === parseInt(ratingFilter))
    if (sentimentFilter !== 'all') list = list.filter((r: any) => r.sentiment === sentimentFilter)
    if (statusFilter !== 'all') list = list.filter((r: any) => r.status === statusFilter)
    if (vendorFilter !== 'all') list = list.filter((r: any) => r.vendor_id === vendorFilter)
    if (locationFilter !== 'all') list = list.filter((r: any) => r.location_id === locationFilter)
    if (serviceLineFilter !== 'all') list = list.filter((r: any) => r.service_line_id === serviceLineFilter)
    if (ownerFilter !== 'all') list = list.filter((r: any) => r.assigned_owner_id === ownerFilter)
    if (dateRangeFilter !== 'all') {
      const range = DATE_RANGES.find(d => d.id === dateRangeFilter)
      if (range?.days) {
        const cutoff = Date.now() - range.days * 86400000
        list = list.filter((r: any) => new Date(r.created_at).getTime() >= cutoff)
      }
    }
    return list
  }, [reviews, platformFilter, ratingFilter, sentimentFilter, statusFilter, vendorFilter, locationFilter, serviceLineFilter, ownerFilter, dateRangeFilter])

  const activeFilterCount = [platformFilter, ratingFilter, sentimentFilter, statusFilter, vendorFilter, locationFilter, serviceLineFilter, ownerFilter, dateRangeFilter].filter(f => f !== 'all').length
  const clearAllFilters = () => {
    setPlatformFilter('all'); setRatingFilter('all'); setSentimentFilter('all'); setStatusFilter('all')
    setVendorFilter('all'); setLocationFilter('all'); setServiceLineFilter('all'); setOwnerFilter('all'); setDateRangeFilter('all')
  }

  const TABS = [
    { id: 'overview',  label: 'Overview' },
    { id: 'reviews',   label: 'Reviews',   count: reviews.length },
    { id: 'requests',  label: 'Requests',  count: requests.length },
    { id: 'nps',       label: 'NPS' },
    { id: 'templates', label: 'Templates', count: templates.length },
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Reputation Hub</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {metrics.avg > 0 ? `${metrics.avg} avg rating · ` : ''}{metrics.total} reviews · {requests.length} requests sent
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setShowAddReviewModal(true)} className="btn-ghost" style={{ fontSize: 13 }}>
              <Plus style={{ width: 14, height: 14 }} /> Add review
            </button>
            {/* Soft-launch HIDE — Reputation outbound build is post-launch.
                RequestReviewModal currently inserts a row + toasts success without sending email/SMS;
                hiding all 4 entry points until SendGrid/Twilio outbound + platform-webhook ingestion ship. */}
            {false && (
              <button onClick={() => setShowRequestModal(true)} className="btn-primary">
                <Send style={{ width: 14, height: 14 }} /> Request review
              </button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border-subtle)' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => switchTab(tab.id)}
              style={{ padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400, color: activeTab === tab.id ? 'var(--ink-primary)' : 'var(--ink-tertiary)', borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent', transition: 'all 150ms ease', display: 'flex', alignItems: 'center', gap: 6 }}>
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 100, background: activeTab === tab.id ? 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' : 'var(--border-subtle)', color: activeTab === tab.id ? 'var(--accent-text)' : 'var(--ink-tertiary)' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>

        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Stat cards — auto-fit so they reflow on narrow viewports. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14 }}>
              {[
                { label: 'Overall rating',   value: metrics.avg > 0 ? metrics.avg.toFixed(1) : '—', sub: 'Across all platforms', color: 'var(--warning)', icon: <Star style={{ width: 18, height: 18, fill: 'var(--warning)', color: 'var(--warning)' }} /> },
                { label: 'Total reviews',    value: metrics.total,         sub: metrics.responded > 0 ? `${metrics.responded} responded` : '—', color: 'var(--ink-primary)', icon: <MessageSquare style={{ width: 18, height: 18, color: 'var(--ink-secondary)' }} /> },
                { label: 'New this week',    value: metrics.newThisWeek,   sub: 'Last 7 days', color: 'var(--info)', icon: <TrendingUp style={{ width: 18, height: 18, color: 'var(--info)' }} /> },
                { label: 'New this month',   value: metrics.newThisMonth,  sub: 'Last 30 days', color: 'var(--accent-text)', icon: <Calendar style={{ width: 18, height: 18, color: 'var(--accent-text)' }} /> },
                { label: 'Pending responses', value: metrics.pending,      sub: metrics.pending > 0 ? 'Needs attention' : 'All caught up', color: metrics.pending > 0 ? 'var(--warning)' : 'var(--ink-tertiary)', icon: <AlertCircle style={{ width: 18, height: 18, color: metrics.pending > 0 ? 'var(--warning)' : 'var(--ink-tertiary)' }} /> },
                { label: 'Negative reviews', value: metrics.negative,      sub: metrics.total > 0 ? `${Math.round(metrics.negative / metrics.total * 100)}% of total` : '—', color: 'var(--danger)', icon: <AlertCircle style={{ width: 18, height: 18, color: 'var(--danger)' }} /> },
                { label: 'Positive reviews', value: metrics.positive,      sub: metrics.total > 0 ? `${Math.round(metrics.positive / metrics.total * 100)}% of total` : '—', color: 'var(--positive)', icon: <ThumbsUp style={{ width: 18, height: 18, color: 'var(--positive)' }} /> },
                { label: 'NPS score',        value: npsScore !== null ? npsScore : '—', sub: npsScore !== null ? (npsScore >= 50 ? 'Excellent' : npsScore >= 0 ? 'Good' : 'Needs work') : 'No surveys yet', color: npsScore !== null ? (npsScore >= 50 ? 'var(--positive)' : npsScore >= 0 ? 'var(--warning)' : 'var(--danger)') : 'var(--ink-tertiary)', icon: <TrendingUp style={{ width: 18, height: 18, color: 'var(--info)' }} /> },
              ].map(m => (
                <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '18px', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: -20, right: -20, width: 70, height: 70, background: `radial-gradient(circle, color-mix(in srgb, ${m.color} var(--status-tint-fill), transparent) 0%, transparent 70%)` }} />
                  <div style={{ marginBottom: 10 }}>{m.icon}</div>
                  <p style={{ fontSize: 28, fontWeight: 900, color: m.color, margin: '0 0 4px', letterSpacing: '-0.03em' }}>{m.value}</p>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 3px' }}>{m.label}</p>
                  <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{m.sub}</p>
                </div>
              ))}
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '20px 24px' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 16px' }}>Platform breakdown</h3>
              {Object.keys(metrics.byPlatform).length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0, textAlign: 'center', padding: '20px 0' }}>No reviews yet</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {Object.entries(metrics.byPlatform).map(([platformId, data]: any) => {
                    const platform = PLATFORMS.find(p => p.id === platformId)
                    const pct = (data.count / metrics.total) * 100
                    return (
                      <div key={platformId} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <span style={{ fontSize: 18, width: 24, textAlign: 'center' }}>{platform?.icon || '⭐'}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', width: 90 }}>{platform?.label || platformId}</span>
                        <div style={{ flex: 1, height: 6, background: 'var(--border-subtle)', borderRadius: 100, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: platform?.color || 'var(--accent)', borderRadius: 100 }} />
                        </div>
                        <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', width: 30, textAlign: 'right' }}>{data.count}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Star style={{ width: 12, height: 12, fill: 'var(--warning)', color: 'var(--warning)' }} />
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--warning)' }}>{data.avg.toFixed(1)}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            {reviews.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>Recent reviews</h3>
                  <button onClick={() => switchTab('reviews')} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    View all <ArrowUpRight style={{ width: 12, height: 12 }} />
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {reviews.slice(0, 3).map((r: any) => (
                    <ReviewCard key={r.id} review={r} onClick={() => setSelectedReview(r)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'reviews' && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setPlatformFilter('all')} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: platformFilter === 'all' ? 'var(--accent)' : 'var(--hover-bg)', color: platformFilter === 'all' ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: platformFilter === 'all' ? 'none' : '1px solid var(--border-subtle)' }}>All platforms</button>
                {PLATFORMS.map(p => (
                  <button key={p.id} onClick={() => setPlatformFilter(p.id)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: platformFilter === p.id ? `color-mix(in srgb, ${p.color} var(--status-tint-strong), transparent)` : 'var(--hover-bg)', color: platformFilter === p.id ? p.color : 'var(--ink-tertiary)', border: platformFilter === p.id ? `1px solid color-mix(in srgb, ${p.color} var(--status-tint-edge), transparent)` : '1px solid var(--border-subtle)' }}>
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setRatingFilter('all')} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: ratingFilter === 'all' ? 'var(--accent)' : 'var(--hover-bg)', color: ratingFilter === 'all' ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: ratingFilter === 'all' ? 'none' : '1px solid var(--border-subtle)' }}>All stars</button>
                {[5, 4, 3, 2, 1].map(n => (
                  <button key={n} onClick={() => setRatingFilter(n.toString())} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: ratingFilter === n.toString() ? 'color-mix(in srgb, var(--warning) var(--status-tint-fill), transparent)' : 'var(--hover-bg)', color: ratingFilter === n.toString() ? 'var(--warning)' : 'var(--ink-tertiary)', border: ratingFilter === n.toString() ? '1px solid color-mix(in srgb, var(--warning) var(--status-tint-edge), transparent)' : '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 3 }}>
                    {n} <Star style={{ width: 11, height: 11, fill: ratingFilter === n.toString() ? 'var(--warning)' : 'none', color: ratingFilter === n.toString() ? 'var(--warning)' : 'var(--ink-tertiary)' }} />
                  </button>
                ))}
                <span style={{ width: 1, height: 18, background: 'var(--border-default)', margin: '0 4px' }} />
                {DATE_RANGES.map(d => (
                  <button key={d.id} onClick={() => setDateRangeFilter(d.id)} style={{ padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: dateRangeFilter === d.id ? 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)' : 'var(--hover-bg)', color: dateRangeFilter === d.id ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: dateRangeFilter === d.id ? '1px solid color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : '1px solid var(--border-subtle)' }}>
                    {d.label}
                  </button>
                ))}
                <button onClick={() => setShowMoreFilters(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: showMoreFilters ? 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)' : 'var(--hover-bg)', color: showMoreFilters ? 'var(--accent-text)' : 'var(--ink-tertiary)', border: showMoreFilters ? '1px solid color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : '1px solid var(--border-subtle)' }}>
                  <Filter style={{ width: 11, height: 11 }} /> More filters
                  {activeFilterCount > 2 && <span style={{ background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 10, fontWeight: 700, borderRadius: 100, padding: '0 5px' }}>{activeFilterCount}</span>}
                </button>
                {activeFilterCount > 0 && (
                  <button onClick={clearAllFilters}
                    style={{ padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'none', color: 'var(--ink-tertiary)', border: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                    Clear all
                  </button>
                )}
              </div>
              {showMoreFilters && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, padding: '10px 12px', background: 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)', border: '1px solid color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)', borderRadius: 10 }}>
                  <select value={sentimentFilter} onChange={e => setSentimentFilter(e.target.value)}
                    style={{ padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                    <option value="all">Any sentiment</option>
                    {SENTIMENTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    style={{ padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                    <option value="all">Any status</option>
                    {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}
                    style={{ padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                    <option value="all">Any vendor</option>
                    {vendors.map((v: any) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  <select value={locationFilter} onChange={e => setLocationFilter(e.target.value)}
                    style={{ padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                    <option value="all">Any location</option>
                    {locations.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  <select value={serviceLineFilter} onChange={e => setServiceLineFilter(e.target.value)}
                    style={{ padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                    <option value="all">Any service line</option>
                    {serviceLines.map((sl: any) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
                  </select>
                  <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
                    style={{ gridColumn: 'span 2', padding: '6px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 7, fontSize: 12, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
                    <option value="all">Any assigned owner</option>
                    {profiles.map((p: any) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                </div>
              )}
            </div>
            {reviewsLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)' }}>Loading...</div>
            ) : filteredReviews.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <Star style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>{activeFilterCount > 0 ? 'No reviews match your filters' : 'No reviews yet'}</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>{activeFilterCount > 0 ? 'Try different filters' : 'Add a manual review or log review requests to start collecting reviews'}</p>
                {activeFilterCount === 0 && (
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button onClick={() => setShowAddReviewModal(true)} className="btn-ghost"><Plus style={{ width: 14, height: 14 }} /> Add review</button>
                    {/* Soft-launch HIDE — Reputation outbound build is post-launch */}
                    {false && (
                      <button onClick={() => setShowRequestModal(true)} className="btn-primary"><Send style={{ width: 14, height: 14 }} /> Request review</button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {filteredReviews.map((r: any) => (
                  <ReviewCard key={r.id} review={r} onClick={() => setSelectedReview(r)} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'requests' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{requests.length} requests sent</p>
              {/* Soft-launch HIDE — Reputation outbound build is post-launch */}
              {false && (
                <button onClick={() => setShowRequestModal(true)} className="btn-primary" style={{ fontSize: 13 }}>
                  <Send style={{ width: 14, height: 14 }} /> New request
                </button>
              )}
            </div>
            {requests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <Send style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>No requests sent yet</p>
                {/* Soft-launch HIDE — Reputation outbound build is post-launch */}
                {false && (
                  <button onClick={() => setShowRequestModal(true)} className="btn-primary" style={{ margin: '0 auto' }}>
                    <Send style={{ width: 14, height: 14 }} /> Send first request
                  </button>
                )}
              </div>
            ) : (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
                {/* Column header — desktop only. Cards on mobile are self-labeling. */}
                {!isMobile && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 120px 80px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
                  {['Customer', 'Channel', 'Platforms', 'Status', 'Sent'].map((h, i) => (
                    <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
                  ))}
                </div>
                )}
                {requests.map((req: any, i: number) => {
                  const STATUS_CFG: Record<string, { color: string; bg: string }> = {
                    pending: { color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)' },
                    sent: { color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) var(--status-tint-dim), transparent)' },
                    opened: { color: 'var(--accent-text)', bg: 'color-mix(in srgb, var(--accent) var(--status-tint-dim), transparent)' },
                    clicked: { color: 'var(--warning)', bg: 'color-mix(in srgb, var(--warning) var(--status-tint-dim), transparent)' },
                    reviewed: { color: 'var(--positive)', bg: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)' },
                  }
                  const sc = STATUS_CFG[req.status] || STATUS_CFG.pending
                  const isLast = i === requests.length - 1

                  // Mobile card. Read-only list (no row click, no per-row
                  // actions). Customer (primary) + status badge top-right.
                  // Channel + platforms grouped as a meta line; sent date
                  // de-emphasized.
                  if (isMobile) {
                    return (
                      <div key={req.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--hover-bg)' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.contact_name}</p>
                            {req.contact_email && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.contact_email}</p>}
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: sc.bg, color: sc.color, textTransform: 'capitalize', alignSelf: 'flex-start', flexShrink: 0 }}>{req.status}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{req.channel === 'email' ? '✉️ Email' : '📱 SMS'}</span>
                          {(req.platforms || []).length > 0 && (
                            <>
                              <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>·</span>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {(req.platforms || []).map((p: string) => {
                                  const platform = PLATFORMS.find(pl => pl.id === p)
                                  return <span key={p} style={{ fontSize: 12 }}>{platform?.icon || p}</span>
                                })}
                              </div>
                            </>
                          )}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Sent {formatRelativeDate(req.sent_at || req.created_at)}</span>
                      </div>
                    )
                  }

                  // Desktop grid row — unchanged.
                  return (
                    <div key={req.id} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 120px 120px 80px', padding: '13px 16px', borderBottom: i < requests.length - 1 ? '1px solid var(--hover-bg)' : 'none', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{req.contact_name}</p>
                        {req.contact_email && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{req.contact_email}</p>}
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', textTransform: 'capitalize' }}>{req.channel === 'email' ? '✉️ Email' : '📱 SMS'}</span>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(req.platforms || []).map((p: string) => {
                          const platform = PLATFORMS.find(pl => pl.id === p)
                          return <span key={p} style={{ fontSize: 12 }}>{platform?.icon || p}</span>
                        })}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: sc.bg, color: sc.color, textTransform: 'capitalize', width: 'fit-content' }}>{req.status}</span>
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(req.sent_at || req.created_at)}</span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'nps' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* NPS stat cards — auto-fit so they reflow on narrow viewports. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14 }}>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '24px', textAlign: 'center' }}>
                <p style={{ fontSize: 48, fontWeight: 900, color: npsScore !== null ? (npsScore >= 50 ? 'var(--positive)' : npsScore >= 0 ? 'var(--warning)' : 'var(--danger)') : 'var(--border-default)', margin: '0 0 6px', letterSpacing: '-0.04em' }}>
                  {npsScore !== null ? npsScore : '—'}
                </p>
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>NPS Score</p>
                <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{nps.filter((n: any) => n.score !== null).length} responses</p>
              </div>
              {NPS_CATEGORIES.map(cat => {
                const count = nps.filter((n: any) => n.score !== null && n.score >= cat.range[0] && n.score <= cat.range[1]).length
                const pct = nps.filter((n: any) => n.score !== null).length > 0 ? Math.round(count / nps.filter((n: any) => n.score !== null).length * 100) : 0
                return (
                  <div key={cat.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '20px', textAlign: 'center' }}>
                    <p style={{ fontSize: 32, fontWeight: 900, color: cat.color, margin: '0 0 6px', letterSpacing: '-0.02em' }}>{count}</p>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 3px' }}>{cat.label}s</p>
                    <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>{pct}% of responses</p>
                  </div>
                )
              })}
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, padding: '18px 22px' }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 8px' }}>How NPS works</h3>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', lineHeight: 1.6, margin: '0 0 12px' }}>NPS (Net Promoter Score) measures customer loyalty. Customers rate 0–10: Promoters (9–10) love you, Passives (7–8) are satisfied, Detractors (0–6) are unhappy. Your score = % Promoters − % Detractors.</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ color: 'var(--danger)', label: '0–6 · Detractors', sub: 'Unhappy customers' }, { color: 'var(--warning)', label: '7–8 · Passives', sub: 'Satisfied, not loyal' }, { color: 'var(--positive)', label: '9–10 · Promoters', sub: 'Love you, refer others' }].map(item => (
                  <div key={item.label} style={{ flex: 1, padding: '10px 14px', background: `color-mix(in srgb, ${item.color} var(--status-tint-faint), transparent)`, border: `1px solid color-mix(in srgb, ${item.color} var(--status-tint-fill), transparent)`, borderRadius: 10, textAlign: 'center' }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: item.color, margin: '0 0 2px' }}>{item.label}</p>
                    <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0 }}>{item.sub}</p>
                  </div>
                ))}
              </div>
            </div>
            {nps.filter((n: any) => n.score !== null).length > 0 && (
              <div>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 14px' }}>Recent responses</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {nps.filter((n: any) => n.score !== null).map((n: any) => {
                    const cat = getNPSCategory(n.score)
                    return (
                      <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 12, background: `color-mix(in srgb, ${cat.color} var(--status-tint-fill), transparent)`, border: `1px solid color-mix(in srgb, ${cat.color} var(--status-tint-strong), transparent)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 16, fontWeight: 900, color: cat.color }}>{n.score}</span>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{n.contact_name || 'Anonymous'}</p>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: `color-mix(in srgb, ${cat.color} var(--status-tint-fill), transparent)`, color: cat.color }}>{cat.label}</span>
                            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginLeft: 'auto' }}>{formatRelativeDate(n.responded_at || n.created_at)}</span>
                          </div>
                          {n.feedback && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, lineHeight: 1.5 }}>"{n.feedback}"</p>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
            {nps.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 24px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14 }}>
                <TrendingUp style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>No NPS data yet</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>NPS surveys will appear here once customers respond</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>
                Response templates · {templates.length} total{templates.filter((t: any) => t.is_active).length !== templates.length ? ` · ${templates.filter((t: any) => t.is_active).length} active` : ''}
              </p>
              <button onClick={() => { setEditingTemplate(null); setShowTemplateModal(true) }} className="btn-primary" style={{ fontSize: 13 }}>
                <Plus style={{ width: 14, height: 14 }} /> New template
              </button>
            </div>
            {templates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <FileText style={{ width: 36, height: 36, color: 'var(--border-default)', margin: '0 auto 12px', display: 'block' }} />
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-tertiary)', margin: '0 0 6px' }}>No templates yet</p>
                <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>Create response templates to speed up review responses. Templates are filterable by rating, platform, and service line.</p>
                <button onClick={() => { setEditingTemplate(null); setShowTemplateModal(true) }} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> First template
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {templates.map((t: any) => (
                  <div key={t.id} onClick={() => { setEditingTemplate(t); setShowTemplateModal(true) }}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, cursor: 'pointer', transition: 'border-color 150ms ease' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: 60, flexShrink: 0 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--warning)' }}>{t.rating_type}</span>
                      <Star style={{ width: 14, height: 14, fill: 'var(--warning)', color: 'var(--warning)' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{t.name}</p>
                        {!t.is_active && (
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 100, background: 'var(--border-subtle)', color: 'var(--ink-tertiary)' }}>Inactive</span>
                        )}
                        {t.tone && (
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: 'color-mix(in srgb, var(--accent) var(--status-tint-dim), transparent)', color: 'var(--accent-text)' }}>{t.tone}</span>
                        )}
                        {t.platform && (
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: 'var(--hover-bg)', color: 'var(--ink-tertiary)' }}>
                            {PLATFORMS.find(p => p.id === t.platform)?.label || t.platform}
                          </span>
                        )}
                        {t.service_line?.name && (
                          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', color: 'var(--positive)' }}>{t.service_line.name}</span>
                        )}
                      </div>
                      <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.template_body}</p>
                    </div>
                    <button onClick={e => { e.stopPropagation(); setEditingTemplate(t); setShowTemplateModal(true) }} aria-label="Edit"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 6, display: 'flex', borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-primary)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                      <Edit2 style={{ width: 14, height: 14 }} />
                    </button>
                    <button onClick={e => { e.stopPropagation(); if (confirm(`Delete template "${t.name}"?`)) deleteTemplateMutation.mutate(t.id) }} aria-label="Delete"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 6, display: 'flex', borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                      onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                      <Trash2 style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showRequestModal && (
          <RequestReviewModal onClose={() => setShowRequestModal(false)} onCreated={() => queryClient.invalidateQueries({ queryKey: ['review-requests'] })} contacts={contacts} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showAddReviewModal && (
          <AddReviewModal
            onClose={() => setShowAddReviewModal(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['reviews'] })}
            vendors={vendors} serviceLines={serviceLines} locations={locations} profiles={profiles} fieldJobs={fieldJobs}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showTemplateModal && (
          <TemplateModal
            onClose={() => { setShowTemplateModal(false); setEditingTemplate(null) }}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['review-templates'] })}
            existing={editingTemplate}
            serviceLines={serviceLines}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {selectedReview && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} exit={{ opacity: 0 }}
              onClick={() => setSelectedReview(null)}
              style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', zIndex: 140 }} />
            <ReviewDrawer
              review={selectedReview}
              onClose={() => setSelectedReview(null)}
              onUpdate={handleReviewUpdate}
              onDelete={(id: string) => deleteReviewMutation.mutate(id)}
              vendors={vendors} serviceLines={serviceLines} locations={locations}
              profiles={profiles} fieldJobs={fieldJobs} templates={templates}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  )
}